// Imports for external libraries go here.
import { useMemo, useState, useEffect, useRef } from 'react';
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { SearchFormViewProps } from './NGSearchForm.types';
import { StyledSearchFormDesktopWrapperDiv, StyledSearchFormMobileWrapperDiv } from './NGSearchForm.styles';
import { SearchPrimaryBlock } from './NGSearchForm.primary'; //search form top block consist of datepicker and destination molecules
import { SearchSecondaryBlock } from './NGSearchForm.secondry'; //search from down block
import { useCheckBreakpoint } from '../../hooks/lib/useCheckBreakpoint';
import clsx from 'clsx';
import React from 'react';
import { useConditionalPageContextSERP } from '../../hooks';
import { isGreaterChinaLocale } from '@marriott/shared/mi-helper-utils';
import { DropDownModal } from '@marriott/mi-ui-library';
import { lockBodyScroll, unlockBodyScroll } from '../../utils/src/utils';
import { SearchFormActiveState } from '../../constants';
import { SearchFormCTAGroup } from './NGSearchFormCTAGroup';
import { useSearchFormStore } from '../../modules/store/searchFormStore';
import { ConversationalAISearchBanner } from '../../molecules/ConversationalAISearchBanner';
import { NGUsePoints } from '../../molecules/NGUsePoints';
import { MAP_VIEW_SESSION_KEY } from '../../modules/store/store.constants';

export const SearchFormView = React.memo((props: SearchFormViewProps) => {
  const { model, hideDestination, sessionData, isNonStickyVariant, isStickySearchForm, isNextGenHP } = props;
  const { isBulgariDomain, currentLocale } = useConditionalPageContextSERP();

  const isDesktopView = useCheckBreakpoint('viewportL');
  const isTabletView = useCheckBreakpoint('viewportM');
  const isAvailabilityScenario = hideDestination;

  // Get search form active state from store (for NextGen standard inactive/active styling)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isSearchFormActiveState = useSearchFormStore((state: any) => state?.isSearchFormActiveState);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setIsSearchFormActiveState = useSearchFormStore((state: any) => state?.setIsSearchFormActiveState);

  // Mobile modal state management
  // formModalState controls the main search form modal visibility
  // currentFormState controls which step of the flow we're in (destination → calendar → form)
  const [formModalState, setMobileFormModalState] = useState(false);
  const [isDatesSelected, setIsDatesSelected] = useState(false);
  const [isDestinationParsed, setIsDestinationParsed] = useState('');
  const [currentFormState, setCurrentFormState] = useState<SearchFormActiveState>(SearchFormActiveState.CLOSED_STATE);
  const [pointsToolTipCloseSignal, setPointsToolTipCloseSignal] = useState(0);
  const isTransitioningRef = useRef(false); // Flag to prevent onCancel during state transitions
  const nextGenHPMobileCalendarRef = useRef(false); // Flag to prevent conflicting handlers from overriding CALENDAR_STATE
  const cameFromFormStateRef = useRef(false); // Flag to track if user came from FORM_STATE (for HP stepper cancel behavior)
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Enhanced scroll prevention that works with iOS keyboards and any dropdown modal
  useEffect(() => {
    if (!isDesktopView) {
      if (formModalState) {
        lockBodyScroll();
      } else {
        unlockBodyScroll();
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(MAP_VIEW_SESSION_KEY) === 'true') {
          document.body.style.overflow = '';
        }
      }
    } else {
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(MAP_VIEW_SESSION_KEY) === 'true') {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'auto';
      }
    }

    return () => {};
  }, [formModalState, isDesktopView]);

  // CNWEB-6664
  // Extract and reuse the existing condition logic from the desktop implementation.
  // Apply the same logic to the non-desktop view for consistency.
  // Logic explanation: The secondary form should only be hidden when all three fields
  // (Room Guest, Special Rates, and Redeem Points) are disabled, or when on Bulgari domain.
  // For desktop we always render the secondary block when this condition is met.
  // For mobile we render it only inside the modal version of the form.
  const isSecondarySearchBlock = useMemo(() => {
    const areRoomGuestAndSpecialRatesDisabled = model?.disableRoomGuest && model?.disableSpecialRates;
    const isRedeemPointsEnabled = model?.enableRedeemPoints;

    return !areRoomGuestAndSpecialRatesDisabled || isRedeemPointsEnabled;
  }, [model?.disableRoomGuest, model?.disableSpecialRates, model?.enableRedeemPoints]);

  const handleInputFieldsChange = () => {
    if (!isNextGenHP && isDesktopView) {
      isSearchFormActiveState && setPointsToolTipCloseSignal(prev => prev + 1);
      return;
    }
    setPointsToolTipCloseSignal(prev => prev + 1);
  };

  const formContent = (isInModal: boolean = false, renderSecondaryBlock: boolean = true) => (
    <>
      <SearchPrimaryBlock
        alertsListArray={model?.alertsListArray}
        isButtonHide={false}
        isDesktopView={isDesktopView}
        isTabletView={isTabletView}
        mobileModalHeading={model?.placeholderTextMobile}
        mobilePlaceHolderText={model?.placeholderTextMobile}
        isSearchFormSticky={props.isSearchFormSticky}
        isAvailabilityScenario={isAvailabilityScenario}
        blacklist={model?.blacklist}
        destinationColStyleOverride={props.destinationColStyleOverride}
        currentFormState={currentFormState}
        setCurrentFormState={newState => {
          // Prevent conflicting handlers from overriding nextGenHP mobile CALENDAR_STATE
          // Only block immediate conflicting transitions back to DESTINATION_STATE
          if (nextGenHPMobileCalendarRef.current && newState === SearchFormActiveState.DESTINATION_STATE) {
            return;
          }

          isTransitioningRef.current = true;
          setCurrentFormState(newState);

          // Handle nextGenHPMobileCalendarRef state management that was moved from child component
          // Set protection flag when transitioning TO CALENDAR_STATE in nextGenHP mobile flow
          if (isNextGenHP && !isDesktopView && newState === SearchFormActiveState.CALENDAR_STATE) {
            nextGenHPMobileCalendarRef.current = true;
          }

          // Only clear protection flag when transitioning AWAY from CALENDAR_STATE
          // This keeps protection active while we stay in CALENDAR_STATE
          if (nextGenHPMobileCalendarRef.current && newState !== SearchFormActiveState.CALENDAR_STATE) {
            nextGenHPMobileCalendarRef.current = false;
          }

          // Reset transition flag after state update
          setTimeout(() => {
            isTransitioningRef.current = false;
          }, 100);
        }}
        setMobileFormModalState={setMobileFormModalState}
        setIsDestinationParsed={setIsDestinationParsed}
        setIsDatesSelected={setIsDatesSelected}
        isDestinationParsed={isDestinationParsed}
        isDatesSelected={isDatesSelected}
        isMobileForm={!isDesktopView}
        formModalState={formModalState}
        isInModal={isInModal}
        isNextGenHP={isNextGenHP}
        isTransitioningRef={isTransitioningRef}
        nextGenHPMobileCalendarRef={nextGenHPMobileCalendarRef}
        cameFromFormStateRef={cameFromFormStateRef}
        onInputFieldsChangeHandle={handleInputFieldsChange}
      />

      {/* NextGen (standard): Show secondary block by default on all viewports
          NextGenHP: Only show on desktop or inside modal (original HP behavior) */}
      {isSecondarySearchBlock && renderSecondaryBlock && (isDesktopView || isInModal || !isNextGenHP) && (
        <SearchSecondaryBlock
          model={model}
          isTabletView={isTabletView}
          isDesktopView={isDesktopView}
          isAvailabilityScenario={isAvailabilityScenario}
          isNextGenHP={isNextGenHP}
          isInModal={isInModal}
          currentFormState={currentFormState}
          setCurrentFormState={setCurrentFormState}
          setMobileFormModalState={setMobileFormModalState}
          onInputFieldsChangeHandle={handleInputFieldsChange}
          pointsToolTipCloseSignal={pointsToolTipCloseSignal}
          isSearchFormActiveState={isSearchFormActiveState}
        />
      )}

      {/* Standard NextGen Desktop with checkbox mode: Wrap checkbox + CTA in a row container */}
      {/* Only show in ACTIVE state */}
      {!isNextGenHP &&
      isDesktopView &&
      !isInModal &&
      isSearchFormActiveState &&
      model?.showPointsAsCheckbox &&
      !model?.disableUsePointCertificates ? (
        <div className="checkbox-cta-row">
          <div className="use-points-checkbox-wrapper">
            <NGUsePoints />
          </div>
          <SearchFormCTAGroup
            customClass={`${!isNextGenHP && 'mr-0'} ${
              isInModal ? 'd-block' : isNextGenHP ? 'd-none d-lg-flex' : 'd-block'
            } searchfrom-submit-btn ${isGreaterChinaLocale(currentLocale) ? 'cn-searchfrom-submit-btn' : ''}`}
            sessionData={sessionData}
            isAvailabilityScenario={isAvailabilityScenario}
            lastElementRef={buttonRef}
            setMobileFormModalState={setMobileFormModalState}
            isInModal={isInModal}
            isNextGenHP={isNextGenHP}
            setCurrentFormState={newState => {
              isTransitioningRef.current = true;
              setCurrentFormState(newState);

              // Handle nextGenHPMobileCalendarRef state management that was moved from child component
              // Set protection flag when transitioning TO CALENDAR_STATE in nextGenHP mobile flow
              if (isNextGenHP && !isDesktopView && newState === SearchFormActiveState.CALENDAR_STATE) {
                nextGenHPMobileCalendarRef.current = true;
              }

              // Clear protection flag when transitioning AWAY from CALENDAR_STATE
              if (nextGenHPMobileCalendarRef.current && newState !== SearchFormActiveState.CALENDAR_STATE) {
                nextGenHPMobileCalendarRef.current = false;
              }

              // Reset transition flag after state update
              setTimeout(() => {
                isTransitioningRef.current = false;
              }, 100);
            }}
          />
        </div>
      ) : (
        /* Standard NextGen Desktop WITHOUT checkbox mode, or other scenarios: Render CTA normally */
        <SearchFormCTAGroup
          customClass={`${!isNextGenHP && 'mr-0'} ${
            isInModal ? 'd-block' : isNextGenHP ? 'd-none d-lg-flex' : 'd-block'
          } searchfrom-submit-btn ${isGreaterChinaLocale(currentLocale) ? 'cn-searchfrom-submit-btn' : ''}`}
          sessionData={sessionData}
          isAvailabilityScenario={isAvailabilityScenario}
          lastElementRef={buttonRef}
          setMobileFormModalState={setMobileFormModalState}
          isInModal={isInModal}
          isNextGenHP={isNextGenHP}
          setCurrentFormState={newState => {
            isTransitioningRef.current = true;
            setCurrentFormState(newState);

            // Handle nextGenHPMobileCalendarRef state management that was moved from child component
            // Set protection flag when transitioning TO CALENDAR_STATE in nextGenHP mobile flow
            if (isNextGenHP && !isDesktopView && newState === SearchFormActiveState.CALENDAR_STATE) {
              nextGenHPMobileCalendarRef.current = true;
            }

            // Clear protection flag when transitioning AWAY from CALENDAR_STATE
            if (nextGenHPMobileCalendarRef.current && newState !== SearchFormActiveState.CALENDAR_STATE) {
              nextGenHPMobileCalendarRef.current = false;
            }

            // Reset transition flag after state update
            setTimeout(() => {
              isTransitioningRef.current = false;
            }, 100);
          }}
        />
      )}
    </>
  );

  return (
    <>
      {/* Desktop View */}
      <StyledSearchFormDesktopWrapperDiv
        $isNextGenHP={isNextGenHP}
        $hideDestination={hideDestination}
        $isBulgariDomain={isBulgariDomain}
        $showPointsAsCheckbox={model?.showPointsAsCheckbox || false}
        className={clsx(
          'd-none d-lg-flex',
          isNextGenHP
            ? props.enableConversationalAISearch && !props.isSticky
              ? 'px-lg-4 pt-lg-4 pb-lg-2'
              : 'p-lg-4'
            : '',
          // Only add flex-wrap for availability scenario when NOT in active state
          // Active state needs nowrap to keep all fields in same row
          isAvailabilityScenario && !isSearchFormActiveState && 'availibilty-search-container flex-wrap',
          // NextGen standard: Add is-active/is-inactive class for click behavior
          !isNextGenHP && (isSearchFormActiveState ? 'is-active' : 'is-inactive')
        )}
        onClick={() => {
          // When in inactive state, clicking anywhere on the form should activate it (like Edit CTA)
          if (!isNextGenHP && !isSearchFormActiveState) {
            setIsSearchFormActiveState?.(true);
          }
        }}
      >
        {formContent(false)}
      </StyledSearchFormDesktopWrapperDiv>

      {/* Mobile View - only render when not desktop to prevent duplicate labels (ARC accessibility) */}
      {!isDesktopView && (
        <StyledSearchFormMobileWrapperDiv
          isNonStickyVariant={isNonStickyVariant}
          $isNextGenHP={isNextGenHP}
          role="tabpanel"
          className={clsx('d-flex d-lg-none', isNextGenHP && !isStickySearchForm && 'searchform-box-shadow')}
        >
          {formContent(false, isAvailabilityScenario)}
          {/* 
          Full search form modal - shown when FORM_STATE is active
          Uses formContent(true) - same markup as desktop
          ID: search-form-modal
          
          For HP: Only show when in FORM_STATE (not during stepper flow)
          For NextGen standard: Show for any non-closed state
        */}
          {currentFormState !== SearchFormActiveState.CLOSED_STATE &&
            formModalState &&
            // HP: Don't show search form modal during stepper (DESTINATION_STATE, CALENDAR_STATE)
            // Only show when user reaches FORM_STATE
            (!isNextGenHP || currentFormState === SearchFormActiveState.FORM_STATE) && (
              <DropDownModal
                id="search-form-modal"
                applyDefaultHeight={false}
                className="mobile-search-form standard"
                role="dialog"
                childrenClassName={'search-form-dropdown'}
                show={formModalState}
                dropdownModalOpenState={true}
                setDropdownModalOpenState={setMobileFormModalState}
                mobileTakeOver={true}
                mobileModalHeading={model?.placeholderTextMobile}
                mobileHeaderEnabled={true}
                dropdownModalOnCLoseFunc={() => {
                  setMobileFormModalState(false);
                  setCurrentFormState(SearchFormActiveState.CLOSED_STATE);
                }}
                customLastElementRef={buttonRef}
              >
                {formContent(true)}
              </DropDownModal>
            )}
        </StyledSearchFormMobileWrapperDiv>
      )}
      {props.enableConversationalAISearch && !props.isSticky && (
        <div className={clsx(isDesktopView ? 'px-5 mx-1' : '')}>
          <ConversationalAISearchBanner isNextGenHp={true} />
        </div>
      )}
    </>
  );
});
